import React from 'react'
import { bindUpdate } from 'wdc-cube-react'

import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import { DataGrid, GridColDef, GridPagination, GridRenderCellParams } from '@mui/x-data-grid'

import {
    bannerSx,
    Breadcrumbs,
    chipSx,
    classToFComponent,
    dataGridRowHoverSx,
    dataGridSx,
    FCClassContext,
    ModuleView,
    PageHeadGrid,
    PageSubtitle,
    PageTitle,
    SkeletonBlock
} from 'src/utils/views'
import { formatNumber } from 'src/utils/formatter-utils'

import AccessAuditScope from '../aa_scopes'
import AccessCard, { AccessCardHeader, AccessEmptyState } from './aa_card'
import AccessChartDailyView from './aa_chart_daily'
import AccessChartMonthlyView from './aa_chart_monthly'
import AccessChartTopPagesView from './aa_chart_top_pages'
import AccessFilterPaneView from './aa_filter_pane'
import AccessKpiTile from './aa_kpi'

const DashboardView = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'minmax(300px, 1fr) minmax(260px, 1fr) minmax(260px, 1fr)',
    gridTemplateRows: 'auto 340px 300px minmax(520px, 1fr)',
    gridTemplateAreas: `
        "head          head    head"
        "summaryTop    monthly ranking"
        "users         daily   daily"
        "summaryBottom detail  detail"
    `,
    gap: 11,
    padding: '26px 28px 42px',
    overflowX: 'hidden',
    overflowY: 'auto',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    '@media (max-width: 1050px)': {
        gridTemplateColumns: 'minmax(0, 1fr)',
        gridTemplateRows:
            'auto minmax(400px, auto) 300px 300px minmax(320px, auto) 300px minmax(460px, auto) minmax(520px, auto)',
        gridTemplateAreas: `
            "head"
            "summaryTop"
            "monthly"
            "ranking"
            "users"
            "daily"
            "summaryBottom"
            "detail"
        `
    },
    '@media (max-width: 720px)': {
        padding: '22px 18px 36px',
        gap: 10
    }
})

const GRID_BASE = { ...dataGridSx, ...dataGridRowHoverSx }

const useStyles = makeStyles()({
    overlay: {
        flexShrink: 0,
        marginRight: 5,
        marginBottom: 5
    },
    summaryTop: {
        gridArea: 'summaryTop',
        display: 'grid',
        gridTemplateRows: 'auto minmax(0, 1fr)',
        gap: 8,
        minWidth: 0,
        minHeight: 0
    },
    summaryBottom: {
        gridArea: 'summaryBottom',
        display: 'grid',
        gridTemplateRows: 'minmax(0, 1fr) minmax(0, 1fr)',
        gap: 8,
        minWidth: 0,
        minHeight: 0
    },
    kpiRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: 8,
        '@media (max-width: 720px)': {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
        }
    },
    monthly: { gridArea: 'monthly', minWidth: 0, minHeight: 0 },
    ranking: { gridArea: 'ranking', minWidth: 0, minHeight: 0 },
    daily: { gridArea: 'daily', minWidth: 0, minHeight: 0 },
    users: { gridArea: 'users', minWidth: 0, minHeight: 0 },
    detail: { gridArea: 'detail', minWidth: 0, minHeight: 0 },
    stretch: { minWidth: 0, minHeight: 0 },
    cardBody: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flexGrow: 1,
        padding: '0 18px 18px'
    },
    banner: {
        margin: '0 18px 12px'
    },
    grid: {
        ...GRID_BASE
    },
    detailFooterContainer: {
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        '& .MuiTablePagination-root': {
            overflow: 'hidden'
        },
        '& .MuiTablePagination-toolbar': {
            minHeight: 40
        }
    },
    detailFooter: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        borderTop: '1px solid var(--border)',
        paddingTop: 8,
        paddingBottom: 2,
        color: 'var(--ink)',
        fontWeight: 700,
        fontSize: 12.5
    },
    detailFooterCell: {
        paddingLeft: 10,
        paddingRight: 10,
        boxSizing: 'border-box',
        overflow: 'hidden'
    }
})

type AccessAuditViewProps = {
    className?: string
    scope: AccessAuditScope
}

type GridCardProps<T> = {
    title: string
    count: React.ReactNode
    rows: T[]
    columns: GridColDef[]
    getRowId: (row: T) => string
    emptyTitle: string
    emptyText: string
    className: string
}

class AccessAuditViewClass implements FCClassContext<AccessAuditViewProps> {
    // :: Fields

    scope!: AccessAuditScope
    classes!: ReturnType<typeof useStyles>['classes']

    // :: Methods

    onSyncState({ scope }: AccessAuditViewProps) {
        this.scope = scope
        this.classes = useStyles().classes
    }

    render({ className, scope }: AccessAuditViewProps) {
        this.scope = bindUpdate(React, scope)
        const { classes } = this

        return (
            <ModuleView className={className}>
                <DashboardView>
                    {this.renderPageHead()}
                    {scope.waiting ? this.renderSkeleton() : this.renderDashboard()}
                </DashboardView>
                <AccessFilterPaneView className={classes.overlay} scope={scope.filterPane} />
            </ModuleView>
        )
    }

    renderPageHead() {
        return (
            <PageHeadGrid>
                <div>
                    <Breadcrumbs>
                        Administração / <b>Auditoria de Acesso</b>
                    </Breadcrumbs>
                    <PageTitle>Auditoria de Acesso</PageTitle>
                    <PageSubtitle>
                        Acompanhe quem acessou quais relatórios no período, compare o volume por departamento e
                        identifique os usuários que ficaram sem nenhum acesso.
                    </PageSubtitle>
                </div>
            </PageHeadGrid>
        )
    }

    renderSkeleton() {
        const { classes } = this

        return (
            <>
                <div className={classes.summaryTop}>
                    <div className={classes.kpiRow}>
                        {[0, 1, 2, 3].map((index) => (
                            <SkeletonBlock key={index} sx={{ height: 84 }} />
                        ))}
                    </div>
                    <SkeletonBlock />
                </div>
                <SkeletonBlock className={classes.monthly} />
                <SkeletonBlock className={classes.ranking} />
                <SkeletonBlock className={classes.users} />
                <SkeletonBlock className={classes.daily} />
                <div className={classes.summaryBottom}>
                    <SkeletonBlock />
                    <SkeletonBlock />
                </div>
                <SkeletonBlock className={classes.detail} />
            </>
        )
    }

    renderDashboard() {
        const { classes, scope } = this

        return (
            <>
                <div className={classes.summaryTop}>
                    <div className={classes.kpiRow}>
                        <AccessKpiTile label="Departamentos" value={scope.totalGroups} />
                        <AccessKpiTile label="Usuários" value={scope.totalUsers} total={scope.totalActiveUsers} />
                        <AccessKpiTile label="Relatórios" value={scope.totalPages} />
                        <AccessKpiTile label="Acessos" value={scope.totalAccesses} />
                    </div>
                    {this.renderGrid({
                        title: 'Acessos por departamento',
                        count: pluralize(scope.byGroup.length, 'departamento com acesso', 'departamentos com acesso'),
                        rows: scope.byGroup,
                        columns: GROUP_COLUMNS,
                        getRowId: rowIdByGroup,
                        emptyTitle: 'Nenhum acesso no período',
                        emptyText: 'Amplie o intervalo de datas ou limpe os filtros no painel da direita.',
                        className: classes.stretch
                    })}
                </div>

                <AccessChartMonthlyView className={classes.monthly} scope={scope.monthly} />
                <AccessChartTopPagesView className={classes.ranking} scope={scope.topPages} />

                {this.renderGrid({
                    title: 'Acessos por usuário',
                    count: pluralize(scope.byUser.length, 'usuário com acesso', 'usuários com acesso'),
                    rows: scope.byUser,
                    columns: USER_COLUMNS,
                    getRowId: rowIdByUser,
                    emptyTitle: 'Nenhum usuário com acesso',
                    emptyText: 'Nenhum usuário registrou acesso no período e nos filtros selecionados.',
                    className: classes.users
                })}

                <AccessChartDailyView className={classes.daily} scope={scope.daily} />

                <div className={classes.summaryBottom}>
                    {this.renderGrid({
                        title: 'Acessos por relatório',
                        count: pluralize(scope.byPage.length, 'relatório acessado', 'relatórios acessados'),
                        rows: scope.byPage,
                        columns: PAGE_COLUMNS,
                        getRowId: rowIdByPage,
                        emptyTitle: 'Nenhum relatório acessado',
                        emptyText: 'Nenhum relatório foi aberto no período e nos filtros selecionados.',
                        className: classes.stretch
                    })}
                    {this.renderGrid({
                        title: 'Usuários sem acessos',
                        count: pluralize(scope.inactiveUsers.length, 'usuário sem acesso', 'usuários sem acesso'),
                        rows: scope.inactiveUsers,
                        columns: INACTIVE_COLUMNS,
                        getRowId: rowIdByUser,
                        emptyTitle: 'Todos os usuários acessaram',
                        emptyText: 'Nenhum usuário ficou sem acessos no período selecionado.',
                        className: classes.stretch
                    })}
                </div>

                {this.renderDetail()}
            </>
        )
    }

    renderDetail() {
        const { classes, scope } = this

        return (
            <AccessCard elevation={0} className={classes.detail}>
                <AccessCardHeader
                    title="Detalhamento dos acessos"
                    count={pluralize(scope.detailRows.length, 'linha exibida', 'linhas exibidas')}
                />
                {scope.detailTruncated && (
                    <Alert
                        className={classes.banner}
                        variant="outlined"
                        icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
                        sx={bannerSx('blue')}
                    >
                        {`Exibindo as ${formatNumber(scope.detailLimit)} linhas mais recentes de ${formatNumber(
                            scope.detailTotal
                        )}. Refine o período ou os filtros para ver o restante.`}
                    </Alert>
                )}
                {scope.detailRows.length === 0 ? (
                    <AccessEmptyState
                        title="Nenhum acesso detalhado"
                        text="Ajuste o período, o departamento, o usuário ou o relatório no painel de filtros."
                    />
                ) : (
                    <div className={classes.cardBody}>
                        <DataGrid
                            className={classes.grid}
                            rows={scope.detailRows}
                            columns={DETAIL_COLUMNS}
                            getRowId={rowIdByDetail}
                            disableRowSelectionOnClick
                            density="compact"
                            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                            pageSizeOptions={[10, 25, 50, 100]}
                            slots={{ footer: DetailFooter }}
                            slotProps={{ footer: { totalAccesses: scope.totalAccesses } }}
                        />
                    </div>
                )}
            </AccessCard>
        )
    }

    renderGrid<T>({ title, count, rows, columns, getRowId, emptyTitle, emptyText, className }: GridCardProps<T>) {
        const { classes } = this

        return (
            <AccessCard elevation={0} className={className}>
                <AccessCardHeader title={title} count={count} />
                {rows.length === 0 ? (
                    <AccessEmptyState title={emptyTitle} text={emptyText} />
                ) : (
                    <div className={classes.cardBody}>
                        <DataGrid
                            className={classes.grid}
                            rows={rows as readonly object[]}
                            columns={columns}
                            getRowId={(row) => getRowId(row as T)}
                            disableRowSelectionOnClick
                            density="compact"
                            hideFooter
                        />
                    </div>
                )}
            </AccessCard>
        )
    }
}

export const AccessAuditView = classToFComponent(AccessAuditViewClass, React)
export default AccessAuditView

// :: Helpers

declare module '@mui/x-data-grid' {
    interface FooterPropsOverrides {
        totalAccesses: number
    }
}

function DetailFooter({ totalAccesses }: { totalAccesses: number }) {
    const { classes } = useStyles()

    return (
        <div className={classes.detailFooterContainer}>
            <div className={classes.detailFooter}>
                {DETAIL_LAYOUT.map((column, index) => (
                    <span
                        key={column.field}
                        className={classes.detailFooterCell}
                        style={{
                            flex: column.flex ? `${column.flex} 1 0` : `0 0 ${column.width}px`,
                            minWidth: column.minWidth,
                            textAlign: column.numeric ? 'right' : 'left'
                        }}
                    >
                        {index === 0 ? 'Total' : column.numeric ? formatNumber(totalAccesses) : ''}
                    </span>
                ))}
            </div>
            <GridPagination />
        </div>
    )
}

function pluralize(count: number, singular: string, plural: string): string {
    return `${formatNumber(count)} ${count === 1 ? singular : plural}`
}

function formatDate(value?: string | null): string {
    if (!value) {
        return '—'
    }

    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('pt-BR')
}

function renderIdentityCell({ value }: GridRenderCellParams) {
    const label = (value as string) ?? '—'
    return (
        <span style={{ fontWeight: 750, overflow: 'hidden', textOverflow: 'ellipsis' }} title={label}>
            {label}
        </span>
    )
}

const accessesColumn: GridColDef = {
    field: 'accesses',
    headerName: 'Acessos',
    width: 80,
    type: 'number',
    valueFormatter: (value) => formatNumber(Number(value))
}

const lastAccessColumn: GridColDef = {
    field: 'lastAccess',
    headerName: 'Último acesso',
    width: 120,
    valueFormatter: (value) => formatDate(value as string | null)
}

const GROUP_COLUMNS: GridColDef[] = [
    { field: 'groupName', headerName: 'Departamento', flex: 1, minWidth: 140, renderCell: renderIdentityCell },
    accessesColumn,
    lastAccessColumn
]

const USER_COLUMNS: GridColDef[] = [
    { field: 'userName', headerName: 'Usuário', flex: 1, minWidth: 130, renderCell: renderIdentityCell },
    accessesColumn,
    { field: 'groupName', headerName: 'Departamento', flex: 1, minWidth: 100 },
    lastAccessColumn
]

const PAGE_COLUMNS: GridColDef[] = [
    { field: 'pageLabel', headerName: 'Relatório', flex: 1, minWidth: 160, renderCell: renderIdentityCell },
    accessesColumn,
    lastAccessColumn
]

const INACTIVE_COLUMNS: GridColDef[] = [
    { field: 'groupName', headerName: 'Departamento', flex: 1, minWidth: 120 },
    { field: 'userName', headerName: 'Usuário sem acesso', flex: 1, minWidth: 200, renderCell: renderIdentityCell },
    {
        field: 'daysSinceLastAccess',
        headerName: 'Dias',
        width: 80,
        type: 'number',
        renderCell: ({ value }) =>
            value == null ? (
                <Chip size="small" label="Nunca" sx={chipSx('gray')} />
            ) : (
                <span>{formatNumber(Number(value))}</span>
            )
    }
]

type DetailColumn = {
    field: string
    headerName: string
    flex?: number
    minWidth?: number
    width?: number
    numeric?: boolean
}

const DETAIL_LAYOUT: DetailColumn[] = [
    { field: 'groupName', headerName: 'Departamento', flex: 1, minWidth: 150 },
    { field: 'userName', headerName: 'Usuário', flex: 1, minWidth: 200 },
    { field: 'accesses', headerName: 'Acessos', width: 80, numeric: true },
    { field: 'pageLabel', headerName: 'Relatório', flex: 1, minWidth: 150 },
    { field: 'day', headerName: 'Data', width: 110 }
]

const DETAIL_FORMATTERS: Record<string, GridColDef['valueFormatter']> = {
    accesses: (value) => formatNumber(Number(value)),
    day: (value) => formatDate(value as string)
}

const DETAIL_COLUMNS: GridColDef[] = DETAIL_LAYOUT.map((column) => ({
    field: column.field,
    headerName: column.headerName,
    flex: column.flex,
    minWidth: column.minWidth,
    width: column.width,
    type: column.numeric ? 'number' : undefined,
    valueFormatter: DETAIL_FORMATTERS[column.field],
    renderCell: column.field === 'groupName' ? renderIdentityCell : undefined
}))

const rowIdByGroup = (row: { groupId: number | null }) => `g-${row.groupId ?? 'none'}`
const rowIdByUser = (row: { userId: number }) => `u-${row.userId}`
const rowIdByPage = (row: { placeCode: string }) => `p-${row.placeCode}`
const rowIdByDetail = (row: unknown) => {
    const detail = row as { groupName: string; userName: string; pageLabel: string; day: string }
    return `${detail.groupName}|${detail.userName}|${detail.pageLabel}|${detail.day}`
}
